<script>
  import { onDestroy } from "svelte";
  import { lang } from '../lib/stores';
  import { select } from "d3-selection";
  import { scaleLinear } from "d3-scale";
  import {
    allWords,
    foundamentalWords,
    commonWords,
    baseWords,
    coffeeWords,
    giornaleHighWords,
  } from "../data/words.js";

  export let data = {};

  let selectedLang;
	const unsubscribe = lang.subscribe((value) => selectedLang = value);
  onDestroy(unsubscribe);

  let width = 0;
  let svgObject;
  let svgWidth = 0;
  let svgHeight = 0;
  const margins = [64, 1, 30, 1];
  const blockHeight = 40;
  const barHeight = 20;
  const bars = () => ([
    {
      title: "Treccani",
      link: "https://www.treccani.it",
      data: [
        {
          color: "url(#dots-grey-0)",
          label: selectedLang === 'it' ? "Lemmi del dizionario" : "Vocabulary entries",
          number: allWords,
          size: 0,
        },
        {
          color: "url(#dots-grey-1)",
          label: selectedLang === 'it' ? "Lessico comune" : "Common vocabulary",
          number: commonWords,
          size: 0,
        },
        {
          color: "url(#dots-grey-2)",
          label: selectedLang === 'it' ? "Vocabolario base" : "Base vocabulary",
          number: baseWords,
          size: 0,
        },
        {
          color: "url(#dots-grey-3)",
          label: selectedLang === 'it' ? "Lessico fondamentale": "Core vocabulary",
          number: foundamentalWords,
          size: 0,
        },
      ],
    },
    {
      title: "Ghali",
      data: [
        {
          color: "url(#dots-accent-2)",
          label: selectedLang === 'it' ? "Tutte le parole di Ghali" : "All the words of Ghali",
          number: Number(data ?? 0),
          size: 0,
        },
      ],
    },
    {
      title: "Coffee Writing",
      link: "https://www.coffeewriting.it/coffee-corretto/parole-parole-parole-ma-quante-ne-utilizziamo-davvero/#:~:text=Quotidianamente%20un%20italiano%20medio%20fa,composto%20da%20sole%202.000%20parole.",
      data: [
        {
          color: "url(#dots-grey-0)",
          label: selectedLang === 'it' ? "Vocabolario comune (Cofee Writing)" : "Common vocabulary (source #1)",
          number: coffeeWords,
          size: 0,
        },
      ],
    },
    {
      title: "Il Giornale",
      link: "https://www.ilgiornale.it/news/l-italiano-medio-usa-solo-800-parole.html",
      data: [
        {
          color: "url(#dots-grey-0)",
          label: selectedLang === 'it' ? "Vocabolario comune (Il Giornale)" : "Common vocabulary (source #2)",
          number: giornaleHighWords,
          size: 0,
        },
      ],
    },
  ]);

  const getLineY1 = (l, i, y) => {
    if (l === 0) {
      return y - 5 - i * 15;
    }
    if (l === bars.length - 1 && i > 0) {
      return y - 5;
    }
    return y;
  };

  const getLineY2 = (y) => {
    return y + barHeight;
  };

  const getTextY = (l, i, y) => {
    if (l === 0) {
      return y - 5 - i * 15;
    }
    if (l === bars().length - 1 && i > 0) {
      return y - 5;
    }
    return y + barHeight / 2;
  };

  const getTextAnchor = (x, w) => {
    if (w - x < 100) {
      return "end";
    }
    return "start";
  };

  const getText = (n, d) => {
    return `${new Intl.NumberFormat("it-IT").format(n)} - ${d}`;
  };

  const draw = () => {
    if (data > 0) {
      bars()[1].data[0].number = Number(data ?? 0);
      svgWidth = width;
      svgHeight =
        blockHeight * bars().length + 5 * bars().length + margins[0] + margins[2];
      // if (svgObject != null) {
        const svg = select('#wordsBarsSvgChart'); // select(svgObject);
        svg.selectAll("g").remove();
        const gLegend = svg
          .append("g")
          .attr("transform", `translate(${margins[3]}, ${margins[0]})`);
        const gShadow = svg
          .append("g")
          .attr("transform", `translate(${margins[3]}, ${margins[0]})`);
        const gData = svg
          .append("g")
          .attr("transform", `translate(${margins[3]}, ${margins[0]})`);
        const gLabels = svg
          .append("g")
          .attr("transform", `translate(${margins[3]}, ${margins[0]})`);

        const barsMaxWidth = svgWidth - margins[1] - margins[3];

        const scale = scaleLinear()
          .domain([0, allWords])
          .range([0, barsMaxWidth]);

        let baseY = 0;

        bars().forEach((b, l) => {
          const y = blockHeight * baseY;
          b.data.forEach((d, i) => {
            gData
              .append("rect")
              .attr("x", scale(0))
              .attr("y", y)
              .attr("height", barHeight)
              .attr("width", scale(d.number) - scale(0))
              .attr("fill", d.color)
              .attr("stroke", "none");
            gData
              .append("rect")
              .attr("x", scale(0))
              .attr("y", y)
              .attr("height", barHeight)
              .attr("width", scale(d.number) - scale(0))
              .attr("fill", "none")
              .attr("stroke", "black")
              .attr("stroke-width", 1)
              .style("stroke", "var(--grey-darker)");
            if (l > 0) {
              gShadow
                .append("line")
                .attr("x1", scale(d.number))
                .attr("x2", scale(d.number))
                .attr("y1", (l - 1) * blockHeight + barHeight)
                .attr(
                  "y2",
                  (l - 1) * blockHeight + barHeight + blockHeight - barHeight,
                )
                .attr("stroke", "black")
                .style("stroke", "var(--grey-darker)")
                .attr("stroke-dasharray", "1 1");
            }
            if (l === 0) {
              gLabels
                .append("line")
                .attr("x1", scale(d.number))
                .attr("x2", scale(d.number))
                .attr("y1", getLineY1(l, i, y))
                .attr("y2", getLineY2(y))
                .attr("class", "value-line")
                .attr("stroke", "black")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "2 2")
                .style("stroke", "var(--grey-darker)");
            }
            gLabels
              .append("text")
              .attr("x", scale(d.number))
              .attr("y", getTextY(l, i, y))
              .attr("dx", scale(d.number) < svgWidth - 100 ? 3 : 0)
              .attr("class", "value-label data")
              .attr("fill", "black")
              .attr("stroke", "none")
              .attr("dominant-baseline", l === 0 ? "auto" : "middle")
              .attr("text-anchor", getTextAnchor(scale(d.number), svgWidth))
              .style("cursor", "pointer")
              .on("click", () =>
                b.link != null ? window.open(b.link) : void 0,
              )
              .text(getText(d.number, d.label));
          });
          baseY += 1;
        });
        const s5000 = scale(10000);
        gLegend
          .append("rect")
          .attr("x", svgWidth - s5000)
          .attr("y", bars().length * blockHeight + 5)
          .attr("width", s5000 - 2)
          .attr("height", barHeight / 2)
          .attr("fill", "black")
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .style("fill", "url(#dots-grey-0)")
          .style("stroke", "var(--grey-dark)");
        gLegend
          .append("line")
          .attr("x1", svgWidth - s5000)
          .attr("x2", svgWidth - 2)
          .attr("y1", bars().length * blockHeight + 15 + barHeight / 2)
          .attr("y2", bars().length * blockHeight + 15 + barHeight / 2)
          .attr("fill", "none")
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .style("stroke", "var(--grey-dark)");
        gLegend
          .append("line")
          .attr("x1", svgWidth - s5000)
          .attr("x2", svgWidth - s5000)
          .attr("y1", bars().length * blockHeight + 15 + barHeight / 2 - 5)
          .attr("y2", bars().length * blockHeight + 15 + barHeight / 2 + 5)
          .attr("fill", "none")
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .style("stroke", "var(--grey-dark)");
        gLegend
          .append("line")
          .attr("x1", svgWidth - 2)
          .attr("x2", svgWidth - 2)
          .attr("y1", bars().length * blockHeight + 15 + barHeight / 2 - 5)
          .attr("y2", bars().length * blockHeight + 15 + barHeight / 2 + 5)
          .attr("fill", "none")
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .style("stroke", "var(--grey-dark)");
        gLegend
          .append("text")
          .attr("x", svgWidth - 1)
          .attr("y", bars().length * blockHeight + 15 + barHeight / 2 + 10)
          .attr("dominant-baseline", "hanging")
          .attr("text-anchor", "end")
          .attr("class", "value-label data")
          .text(`${new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(10000)} ${selectedLang === 'it' ? "parole" : "words"}`);
      // }
    }
  };

  $: data, draw();
  $: width, draw();
  $: selectedLang, draw();
</script>

<section bind:clientWidth={width} class="wordsBars">
  <svg
    bind:this={svgObject}
    id="wordsBarsSvgChart"
    width={svgWidth}
    height={svgHeight}
    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
    class="chart-svg-wordsBars"
  />
</section>

<style>
  .wordsBars {
    display: block;
    height: auto;
    position: relative;
  }
</style>
